/**
 * Symphony GROUP CHAT — the client half of "a group must look like a group" (design doc
 * `docs/2026-10-30-symphony-group-chat-design-and-implementation.md` §3.2, T31).
 *
 * Pure: parse the optional `group` summary a chat-list row or a conversation carries, the optional `author` and
 * `metadata.groupEvent` a message carries, and decide what a row, header and transcript draw. No React, no fetch,
 * so web, iOS (`GroupChat.swift`) and Android (`GroupChat.kt`) can be read side by side, case for case; the tests
 * (`group-chat.test.ts` · `GroupChatTests` · `GroupChatTest`) mirror each other.
 *
 * Parsing is tolerant: anything absent or malformed reads as a PRIVATE chat, which renders exactly as today. And
 * nothing here draws unless `symphonyGroupChat` is on (`lib/group-chat-flag.ts`) — callers pass `enabled`.
 */

import { parseEventRsvps, parseGroupContent, parsePollVotes, type GroupContent, type GroupEventRsvp, type GroupPollVote } from './group-content';

export type GroupParticipantKind = 'HUMAN' | 'MAESTRO';
export type GroupRole = 'OWNER' | 'ADMIN' | 'MEMBER';

export interface GroupParticipant {
  /** The participant's Symphony identity metasite id (a Maestro: its own stable id). */
  id: string;
  kind: GroupParticipantKind;
  displayName: string;
  avatarUrl?: string;
  /** Humans only; unknown → MEMBER. */
  role: GroupRole;
  /** Maestros only: whose assistant it is. */
  ownerId?: string;
  ownerFirstName?: string;
  /** The server marks the caller's own participant row (Phase 3b). */
  you?: boolean;
  /**
   * Phase 5, client-side only: a Maestro the server sent with no name, named by `nameMaestros` after its owner
   * ("Dana's Maestro") or the default ("Maestro"). Its label is then that name alone — never "Dana's Maestro ·
   * Dana's assistant". A person with no name is named by their role the same way ("Group owner").
   */
  defaultNamed?: boolean;
  /**
   * Client-side: the VIEWER's own Maestro (its owner is the viewer). It shares its owner's id, so it is told apart by
   * kind, never by id; it is drawn like any other Maestro and labelled "{agentName} · your assistant".
   */
  mine?: boolean;
}

export interface GroupSummary {
  /** The group's chat (Phase 3b list rows carry it; absent on the 3a fixture rows). */
  conversationId?: string;
  /** The owner's Symphony identity metasite. */
  ownerMetaSiteId?: string;
  /** The caller's role in this group, when the server says. */
  yourRole?: GroupRole;
  name?: string;
  /** Phase 10a: the group's own photo, when it has one — the header and the list row lead with it. */
  photoUrl?: string;
  memberCount: number;
  /** The caller's own participant id, when the server says which one is them. */
  viewerId?: string;
  /** Participant id of the last message's author, for the list row's "Dana: …" preview. */
  lastMessageAuthorId?: string;
  /** Active members only. */
  participants: GroupParticipant[];
  /**
   * People who WERE in the group and are not now — left, removed, or removed by its end (2026-11-01 hands-on: once a group
   * ended, every former member's messages, mentions and "added" lines read "Someone"/"Member"). Display only: never counted,
   * never mentionable, never in the header's avatars or the People list. Name lookups fall back to it (`personById`).
   * iOS / Android `GroupSummary.formerParticipants` mean the same.
   */
  formerParticipants?: GroupParticipant[];
  /**
   * The group has ENDED (2026-10-31 hands-on rerun): it stays a group on screen — its name, photo and people, the
   * transcript — with no composer, and its list row reads "This group has ended". Set from a list row's
   * `endedGroup` (or `group.ended`), a `members` read with `group.active == false`, a GROUP_ENDED refusal or a live
   * GROUP_ENDED event. iOS / Android `GroupSummary.ended` mean the same.
   */
  ended?: true;
}

export type MessageAuthorKind = 'HUMAN' | 'MAESTRO' | 'SYSTEM';
export interface MessageAuthor {
  principalMetaSiteId: string;
  kind: MessageAuthorKind;
  agentOwnerMetaSiteId?: string;
}

export type GroupEventType = 'JOINED' | 'LEFT' | 'MAESTRO_ADDED' | 'MAESTRO_REMOVED' | 'ENDED';
export interface GroupEvent {
  type: GroupEventType;
  participantId?: string;
  displayName?: string;
}

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * The avatar geometry — THE SAME NUMBERS on web, iOS and Android (`GroupChat.swift` / `GroupChat.kt`). `step` is
 * the distance from one avatar's leading edge to the next; `slot` is the leading slot a list row reserves.
 */
export const GROUP_AVATAR = {
  /** Chats page / Chats screen rows. */
  row: { size: 32, step: 12, slot: 80, gap: 12, icon: 14 },
  /** Sidebar, mobile recents, search, drawer recents. */
  compact: { size: 20, step: 8, slot: 52, gap: 8, icon: 12 },
  /** The group header inside the chat. */
  header: { size: 24, step: 12, gap: 8 },
  /** The author avatar on another member's message (the WhatsApp-group sender avatar's size). */
  message: { size: 24 },
  /** A participant row in the participant sheet. */
  sheet: { size: 40, gap: 12 },
  /** Ring in the background colour around every avatar, so overlaps read. */
  ring: 2,
  /** Up to five of the other participants; a sixth or more becomes the `+N` pill. The slots above fit five. */
  maxAvatars: 5,
  /** The `+N` pill. */
  plus: { height: 16, minWidth: 16, paddingX: 4, fontSize: 10, ring: 1.5, overhangX: 6, overhangY: 4 },
} as const;

/** Marks the viewer's own Maestro `mine` (see `GroupParticipant.mine`); the same summary when there is none. */
function markOwnMaestro(summary: GroupSummary): GroupSummary {
  const viewerId = summary.viewerId;
  if (!viewerId || !summary.participants.some((p) => p.kind === 'MAESTRO' && p.ownerId === viewerId && !p.mine)) return summary;
  return { ...summary, participants: summary.participants.map((p) => (p.kind === 'MAESTRO' && p.ownerId === viewerId ? { ...p, mine: true } : p)) };
}

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

function parseParticipant(raw: unknown): GroupParticipant | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const kind = raw.kind === 'HUMAN' || raw.kind === 'MAESTRO' ? raw.kind : null;
  if (!id || !kind) return null;
  const role: GroupRole = raw.role === 'OWNER' || raw.role === 'ADMIN' ? raw.role : 'MEMBER';
  const avatarUrl = str(raw.avatarUrl);
  const ownerId = str(raw.ownerId);
  const ownerFirstName = str(raw.ownerFirstName);
  return {
    id,
    kind,
    displayName: typeof raw.displayName === 'string' ? raw.displayName : '',
    role,
    ...(avatarUrl ? { avatarUrl } : {}),
    ...(ownerId ? { ownerId } : {}),
    ...(ownerFirstName ? { ownerFirstName } : {}),
    ...(raw.you === true ? { you: true } : {}),
  };
}

/** The `group` summary, or null for a private chat (absent, not an object, or no usable participant). */
export function parseGroupSummary(raw: unknown): GroupSummary | null {
  if (!isObject(raw) || !Array.isArray(raw.participants)) return null;
  const participants = raw.participants.map(parseParticipant).filter((p): p is GroupParticipant => p !== null);
  // An ENDED group (VMR #34789) is still a group with no one left in it: the list keeps its row for a member the end
  // removed with `participants` empty and `active` false. A LIVE group with no participants is malformed.
  const ended = raw.ended === true || raw.active === false;
  if (participants.length === 0 && !(ended && (str(raw.conversationId) || str(raw.name)))) return null;
  // `memberCount` counts the people (the viewer included), never the Maestros.
  const count = typeof raw.memberCount === 'number' && Number.isFinite(raw.memberCount) && raw.memberCount > 0 ? Math.floor(raw.memberCount) : participants.filter((p) => p.kind === 'HUMAN').length;
  const name = typeof raw.name === 'string' ? raw.name : undefined;
  // The server names the viewer either way: `viewerId`, or the participant row marked `you`.
  const viewerId = str(raw.viewerId) ?? participants.find((p) => p.you && p.kind === 'HUMAN')?.id;
  const lastMessageAuthorId = str(raw.lastMessageAuthorId);
  const conversationId = str(raw.conversationId);
  const ownerMetaSiteId = str(raw.ownerMetaSiteId);
  const yourRole = raw.yourRole === 'OWNER' || raw.yourRole === 'ADMIN' || raw.yourRole === 'MEMBER' ? raw.yourRole : undefined;
  const photoUrl = str(raw.photoUrl);
  return markOwnMaestro({
    ...(conversationId ? { conversationId } : {}),
    ...(ownerMetaSiteId ? { ownerMetaSiteId } : {}),
    ...(yourRole ? { yourRole } : {}),
    ...(name !== undefined ? { name } : {}),
    ...(photoUrl ? { photoUrl } : {}),
    memberCount: count,
    ...(viewerId ? { viewerId } : {}),
    ...(lastMessageAuthorId ? { lastMessageAuthorId } : {}),
    participants,
    ...(ended ? { ended: true as const } : {}),
  });
}

/**
 * A chats-v1 row's group: `group` (live), else `endedGroup` — the SAME shape, which the serverless list sends for an
 * ended group under its own key so builds that predate it keep showing the row as before — read as ended. Null for a
 * private chat.
 */
export function rowGroupSummary(row: { group?: unknown; endedGroup?: unknown } | null | undefined): GroupSummary | null {
  if (!row) return null;
  const live = parseGroupSummary(row.group);
  if (live) return live;
  const ended = parseGroupSummary(row.endedGroup);
  return ended ? { ...ended, ended: true } : null;
}

/** The same summary flagged ended (a no-op on one that already is). */
export function endedSummary(summary: GroupSummary): GroupSummary {
  return summary.ended ? summary : { ...summary, ended: true };
}

export function parseMessageAuthor(raw: unknown): MessageAuthor | null {
  if (!isObject(raw)) return null;
  const principalMetaSiteId = str(raw.principalMetaSiteId);
  const kind = raw.kind === 'HUMAN' || raw.kind === 'MAESTRO' || raw.kind === 'SYSTEM' ? raw.kind : null;
  if (!principalMetaSiteId || !kind) return null;
  const agentOwnerMetaSiteId = str(raw.agentOwnerMetaSiteId);
  return { principalMetaSiteId, kind, ...(agentOwnerMetaSiteId ? { agentOwnerMetaSiteId } : {}) };
}

/** `metadata.groupEvent`, or null — an unknown type is not a system line. */
export function parseGroupEvent(metadata: unknown): GroupEvent | null {
  if (!isObject(metadata)) return null;
  const raw = metadata.groupEvent;
  if (!isObject(raw)) return null;
  const type = raw.type;
  if (type !== 'JOINED' && type !== 'LEFT' && type !== 'MAESTRO_ADDED' && type !== 'MAESTRO_REMOVED' && type !== 'ENDED') return null;
  const participantId = str(raw.participantId);
  const displayName = str(raw.displayName);
  return { type, ...(participantId ? { participantId } : {}), ...(displayName ? { displayName } : {}) };
}

/**
 * "1 member" / "3 members" (`chat.group.memberCount.one|.other`, the catalog's hand-picked plural pair). iOS / Android
 * `GroupChat.memberCountLine` read the same keys.
 */
export function memberCountLine(count: number, t: Translate): string {
  return t(`chat.group.memberCount.${count === 1 ? 'one' : 'other'}`, { count });
}

/**
 * Whether the header says how many people are in the group: never for an ENDED group (2026-11-01 hands-on: its owner read
 * "1 members" — the end leaves only them, so the count says nothing true about the group). iOS / Android decide the same.
 */
export function showsMemberCount(summary: GroupSummary): boolean {
  return !summary.ended;
}

/** "Dana Levi" → "DL", "amir" → "A", "" → "?". */
export function groupInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = Array.from(parts[0])[0] ?? '';
  if (parts.length === 1) return first.toUpperCase();
  const last = Array.from(parts[parts.length - 1])[0] ?? '';
  return (first + last).toUpperCase();
}

export function firstName(displayName: string): string {
  const trimmed = displayName.trim();
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

const FALLBACK_NAME_KEYS: Record<GroupRole, string> = {
  OWNER: 'chat.group.fallbackName.owner',
  ADMIN: 'chat.group.fallbackName.admin',
  MEMBER: 'chat.group.fallbackName.member',
};

/**
 * A person's name wherever a group draws one (the info screen's People, the `@` picker, a bubble's author line, a
 * reply quote, a preview): their own name, else a ROLE-BASED fallback — "Group owner", "Admin", "Member" — never a
 * blank name over a "?" avatar. iOS `GroupChat.personName` / Android `GroupChat.personName` decide the same.
 */
export function personName(name: string | null | undefined, role: GroupRole | null | undefined, t: Translate): string {
  const trimmed = (name ?? '').trim();
  return trimmed || t(FALLBACK_NAME_KEYS[role ?? 'MEMBER']);
}

/** The short form a title, preview or mention uses: the first name — or the whole fallback ("Group owner"), never "Group". */
export function shortName(p: GroupParticipant): string {
  return p.defaultNamed ? p.displayName.trim() : firstName(p.displayName);
}

const ROLES: readonly GroupRole[] = ['OWNER', 'ADMIN', 'MEMBER'];

/**
 * The name a person's Maestro is named after ("{owner}'s Maestro", "· {owner}'s assistant", an approval's title, "asked
 * by"): their first name — or, for a person with no name of their own, the WHOLE role fallback ("Group owner"), never
 * its first word. symphony-bff names a nameless person by role ("Group owner") and their Maestro "Group owner's
 * Maestro", so a name equal to ANY role's localized fallback is that fallback, kept whole. iOS / Android
 * `GroupChat.ownerShortName` decide the same.
 */
export function ownerShortName(human: { displayName: string; role?: GroupRole | null }, t: Translate): string {
  const trimmed = (human.displayName ?? '').trim();
  if (!trimmed) return personName('', human.role, t);
  if (ROLES.some((r) => t(FALLBACK_NAME_KEYS[r]) === trimmed)) return trimmed;
  return firstName(trimmed);
}

/**
 * Everyone but the viewer, in the server's order (everyone, when the viewer is unknown). Only the viewer's HUMAN row
 * goes: their own Maestro carries the same id (its owner's metasite) and stays, labelled as theirs (`mine`).
 */
export function others(summary: GroupSummary): GroupParticipant[] {
  const viewerId = summary.viewerId;
  return viewerId ? summary.participants.filter((p) => !(p.kind === 'HUMAN' && p.id === viewerId)) : summary.participants;
}

/**
 * The avatars a stack draws (up to five of the others, Maestros included) and its `+N`: every PERSON in the group
 * (`memberCount`, humans only, which counts the viewer) minus the people drawn, so 3 people drawn of 6 is +3. A
 * Maestro's avatar never counts toward it. No `memberCount` → the human participants. iOS/Android
 * `GroupChat.avatarStack` count the same.
 */
export function avatarStack(summary: GroupSummary): { shown: GroupParticipant[]; plus: number } {
  const shown = others(summary).slice(0, GROUP_AVATAR.maxAvatars);
  const total = summary.memberCount > 0 ? summary.memberCount : summary.participants.filter((p) => p.kind === 'HUMAN').length;
  const people = shown.filter((p) => p.kind === 'HUMAN').length;
  return { shown, plus: Math.max(0, total - people) };
}

/**
 * The photo a group's leading image draws, or null for the avatar stack (Phase 10a, WhatsApp-like). `details` is the
 * group's identity from a `members` read, when one is loaded: it is the fresher of the two (an edit in group info
 * lands there before the chats list re-reads), so it wins either way — a removed photo goes back to the stack.
 * iOS `GroupChat.leadingPhoto` / Android `GroupChat.leadingPhoto` decide the same.
 */
export function leadingPhoto(summary: GroupSummary, details?: { photoUrl: string | null } | null): string | null {
  const url = details ? details.photoUrl : summary.photoUrl;
  return url && url.trim() ? url : null;
}

/** The group's name, else the other humans' first names (three, then ", +N"), else today's title. */
export function groupTitle(summary: GroupSummary, fallback: string): string {
  const named = summary.name?.trim();
  if (named) return named;
  const humans = others(summary).filter((p) => p.kind === 'HUMAN').map(shortName).filter(Boolean);
  if (humans.length === 0) return fallback;
  const head = humans.slice(0, 3).join(', ');
  return humans.length > 3 ? `${head}, +${humans.length - 3}` : head;
}

/** `{agentName} · {ownerFirstName}'s assistant` — a Maestro can be renamed, so it is never "Dana's Maestro". */
export function maestroLabel(p: GroupParticipant, t: Translate): string {
  if (p.mine) return t('chat.group.maestroLabelYours', { agentName: p.displayName });
  if (!p.ownerFirstName || p.defaultNamed) return p.displayName;
  // A name that already says whose it is ("Group owner's Maestro") is the label alone, ONCE — never followed by
  // "· Group's assistant". That holds for the owner's own name and for any role fallback a nameless owner is named by.
  const owners = [p.ownerFirstName, ...ROLES.map((r) => t(FALLBACK_NAME_KEYS[r]))];
  if (owners.some((ownerFirstName) => p.displayName === t('chat.group.maestroOwnerName', { ownerFirstName }))) return p.displayName;
  return t('chat.group.maestroLabel', { agentName: p.displayName, ownerFirstName: p.ownerFirstName });
}

export function participantLabel(p: GroupParticipant, t: Translate): string {
  return p.kind === 'MAESTRO' ? maestroLabel(p, t) : p.displayName;
}

/**
 * The name over a member's message and inside a system line: a PERSON by their FIRST name (`ownerShortName` — a role
 * fallback such as "Group owner" stays whole), a Maestro by its label. The full name is only for the info screen and
 * the members list (`participantLabel`). iOS / Android `GroupChat.authorLabel` decide the same.
 */
export function authorLabel(p: GroupParticipant, t: Translate): string {
  return p.kind === 'MAESTRO' ? maestroLabel(p, t) : ownerShortName(p, t);
}

/**
 * The viewer's own Maestro as THIS GROUP names it — the `{maestroAgentName}` of the Notifications picker and hint, never
 * the generic "Maestro": the name the group holds for it (the server's, or the name the viewer gave their agent,
 * `ownAgentName`), else "{viewer's first name}'s Maestro" (`chat.group.maestroOwnerName`, what the other members read),
 * else `ownAgentName`. `summary` is the summary as read, before `nameMaestros`. iOS / Android
 * `GroupNotifications.groupMaestroName` decide the same.
 */
export function groupMaestroName(summary: GroupSummary | null | undefined, ownAgentName: string, t: Translate, genericNames: readonly string[] = []): string {
  const generic = new Set([t('home.map.maestro'), ...genericNames].map((n) => n.trim().toLowerCase()).filter(Boolean));
  const named = (n: string | null | undefined) => {
    const v = (n ?? '').trim();
    return v && !generic.has(v.toLowerCase()) ? v : '';
  };
  const viewer = summary?.viewerId;
  const mine = summary?.participants.find((p) => p.kind === 'MAESTRO' && (p.mine || (!!viewer && p.ownerId === viewer)));
  const own = named(mine?.displayName) || named(ownAgentName);
  if (own) return own;
  const human = viewer ? summary?.participants.find((p) => p.kind === 'HUMAN' && p.id === viewer) : undefined;
  const ownerFirstName = human && human.displayName.trim() ? ownerShortName(human, t) : (mine?.ownerFirstName ?? '').trim();
  if (ownerFirstName) return t('chat.group.maestroOwnerName', { ownerFirstName });
  return ownAgentName.trim() || t('home.map.maestro');
}

/**
 * A PERSON of the group by id — a current member, else someone who was one (`formerParticipants`) — for every place that
 * NAMES someone: a bubble's author, a SYSTEM line, a mention, a reply quote. Never for who may be mentioned or counted.
 */
export function personById(summary: GroupSummary | null | undefined, id: string | null | undefined): GroupParticipant | undefined {
  if (!summary || !id) return undefined;
  return summary.participants.find((p) => p.kind === 'HUMAN' && p.id === id) ?? summary.formerParticipants?.find((p) => p.id === id);
}

export function previewPrefix(summary: GroupSummary, t: Translate): string | null {
  const id = summary.lastMessageAuthorId;
  if (!id) return null;
  if (id === summary.viewerId) return t('chat.group.you');
  const p = summary.participants.find((x) => x.id === id) ?? personById(summary, id);
  if (!p) return null;
  return p.kind === 'MAESTRO' ? maestroLabel(p, t) : shortName(p);
}

export function groupPreview(summary: GroupSummary, text: string, t: Translate): string {
  const author = previewPrefix(summary, t);
  return author ? t('chat.group.previewAuthor', { author, text }) : text;
}

/**
 * The participant who wrote a message — KIND-AWARE (Phase 5). A Maestro's message carries its OWNER's metasite as
 * the principal, the same id as the owner's HUMAN row, so a MAESTRO author resolves only to a MAESTRO participant
 * (its own id, else the one whose owner is `agentOwnerMetaSiteId`), and a HUMAN author only to a HUMAN participant.
 */
export function resolveAuthor(summary: GroupSummary, author: MessageAuthor | null | undefined): GroupParticipant | null {
  if (!author) return null;
  if (author.kind === 'MAESTRO') {
    const maestros = summary.participants.filter((p) => p.kind === 'MAESTRO');
    const byId = maestros.find((p) => p.id === author.principalMetaSiteId);
    if (byId) return byId;
    const owner = author.agentOwnerMetaSiteId ?? author.principalMetaSiteId;
    return maestros.find((p) => p.ownerId === owner) ?? null;
  }
  if (author.kind === 'HUMAN') {
    return personById(summary, author.principalMetaSiteId) ?? null;
  }
  return summary.participants.find((p) => p.id === author.principalMetaSiteId) ?? personById(summary, author.principalMetaSiteId) ?? null;
}

export interface GroupTranscriptMessage {
  id?: string;
  role?: string;
  author?: MessageAuthor | null;
  groupEvent?: GroupEvent | null;
  /** Phase 5: a Maestro's "can't help right now" notice — a centred line, like a system row. */
  maestroUnavailable?: string | null;
  /** Phase 8: a SYSTEM line (work stopped / chain paused) is never part of an author's run. */
  groupMeta?: Pick<GroupMessageMeta, 'workStopped' | 'chainStopped' | 'membersAdded' | 'infoEvent'> | null;
}

/** No author: a USER row is the viewer's own (today's trailing bubble), an assistant row is not. */
export function isFromViewer(summary: GroupSummary, message: GroupTranscriptMessage): boolean {
  const author = message.author;
  if (!author) return message.role === 'USER';
  return author.kind === 'HUMAN' && !!summary.viewerId && author.principalMetaSiteId === summary.viewerId;
}

const authorKey = (a: MessageAuthor) => `${a.kind}:${a.principalMetaSiteId}`;

/**
 * The ids of the messages that SHOW their author's avatar and name: the first of each run of consecutive messages
 * from the same author who is not the viewer. A system line, a viewer's row or a different author breaks a run.
 * `messages` is the VISIBLE transcript, chronological.
 */
export function authorRunHeads(summary: GroupSummary, messages: GroupTranscriptMessage[]): Set<string> {
  const heads = new Set<string>();
  let prevKey: string | null = null;
  for (const m of messages) {
    if (m.groupEvent || m.maestroUnavailable || m.groupMeta?.workStopped || m.groupMeta?.chainStopped || m.groupMeta?.membersAdded || m.groupMeta?.infoEvent || !m.author || isFromViewer(summary, m) || m.author.kind === 'SYSTEM') {
      prevKey = null;
      continue;
    }
    const key = authorKey(m.author);
    if (key !== prevKey && m.id) heads.add(m.id);
    prevKey = key;
  }
  return heads;
}

const EVENT_KEYS: Record<GroupEventType, string> = {
  JOINED: 'chat.group.event.joined',
  LEFT: 'chat.group.event.left',
  MAESTRO_ADDED: 'chat.group.event.maestroAdded',
  MAESTRO_REMOVED: 'chat.group.event.maestroRemoved',
  ENDED: 'chat.group.event.ended',
};

/** A membership line; a name nobody can resolve reads "Someone" (a Maestro: the default "Maestro"), never empty. */
export function eventLine(event: GroupEvent, summary: GroupSummary | null, t: Translate): string {
  const maestro = event.type === 'MAESTRO_ADDED' || event.type === 'MAESTRO_REMOVED';
  // The viewer reads "You" (as every info line does); a person by their FIRST name, never the full one.
  if (!maestro && event.participantId && summary?.viewerId && event.participantId === summary.viewerId) {
    return t(EVENT_KEYS[event.type], { name: t('chat.group.you') });
  }
  const p = event.participantId ? summary?.participants.find((x) => x.id === event.participantId) ?? (maestro ? undefined : personById(summary, event.participantId)) : undefined;
  const own = (event.displayName ?? '').trim();
  const resolved = (p ? authorLabel(p, t) : own && !maestro ? ownerShortName({ displayName: own }, t) : own).trim();
  const name = resolved || t(maestro ? 'home.map.maestro' : 'chat.group.someone');
  return t(EVENT_KEYS[event.type], { name });
}

/**
 * What a chat-list row draws. `enabled` is `symphonyGroupChat`; off, or a private chat, returns today's title and
 * preview unchanged and no group look at all.
 */
export interface GroupRowPresentation {
  title: string;
  preview: string;
  group: { shown: GroupParticipant[]; plus: number } | null;
}
export function groupRowPresentation(
  enabled: boolean,
  summary: GroupSummary | null | undefined,
  todayTitle: string,
  todayPreview: string,
  t: Translate,
): GroupRowPresentation {
  if (!enabled || !summary) return { title: todayTitle, preview: todayPreview, group: null };
  // An ended group keeps its name and avatars; its preview says it has ended (iOS / Android draw the same).
  if (summary.ended) return { title: groupTitle(summary, todayTitle), preview: t('chat.group.lost.ended.title'), group: avatarStack(summary) };
  return {
    title: groupTitle(summary, todayTitle),
    preview: todayPreview ? groupPreview(summary, todayPreview, t) : todayPreview,
    group: avatarStack(summary),
  };
}

// ─── Phase 3b: per-member state, the `members` answer, and a message off the wire ───────────────────────────

/**
 * A JOINED group's per-member presentation state (`viewerState` on the list row). Present only for a group the
 * viewer joined — never on the owner's own row, which keeps today's pin and read paths. Absent or malformed → null.
 */
export interface GroupViewerState {
  pinned: boolean;
  muted: boolean;
  archived: boolean;
  hasUnread: boolean;
  lastReadDate: string | null;
  lastReadMessageId: string | null;
}
export function parseViewerState(raw: unknown): GroupViewerState | null {
  if (!isObject(raw)) return null;
  return {
    pinned: raw.pinned === true,
    muted: raw.muted === true,
    archived: raw.archived === true,
    hasUnread: raw.hasUnread === true,
    lastReadDate: str(raw.lastReadDate) ?? null,
    lastReadMessageId: str(raw.lastReadMessageId) ?? null,
  };
}

/** One member as `members` / `accept-invite` answer it (MemberWire). */
export interface GroupMember {
  metaSiteId: string;
  name: string;
  avatarUrl: string | null;
  role: GroupRole;
  joinedDate: string | null;
  historyFrom: string | null;
  you: boolean;
}
export function parseGroupMember(raw: unknown): GroupMember | null {
  if (!isObject(raw)) return null;
  const metaSiteId = str(raw.metaSiteId);
  if (!metaSiteId) return null;
  const role: GroupRole = raw.role === 'OWNER' || raw.role === 'ADMIN' ? raw.role : 'MEMBER';
  return {
    metaSiteId,
    name: typeof raw.name === 'string' ? raw.name : '',
    avatarUrl: str(raw.avatarUrl) ?? null,
    role,
    joinedDate: str(raw.joinedDate) ?? null,
    historyFrom: str(raw.historyFrom) ?? null,
    you: raw.you === true,
  };
}

/** GroupSummaryWire — the group itself, without its people. */
export interface GroupInfo {
  conversationId: string;
  name: string;
  active: boolean;
  ownerMetaSiteId: string | null;
  createdDate: string | null;
  /** Phase 10a: null = no photo. */
  photoUrl: string | null;
}
export function parseGroupInfo(raw: unknown): GroupInfo | null {
  if (!isObject(raw)) return null;
  const conversationId = str(raw.conversationId);
  if (!conversationId) return null;
  return {
    conversationId,
    name: typeof raw.name === 'string' ? raw.name : '',
    active: raw.active !== false,
    ownerMetaSiteId: str(raw.ownerMetaSiteId) ?? null,
    createdDate: str(raw.createdDate) ?? null,
    photoUrl: str(raw.photoUrl) ?? null,
  };
}

/**
 * The SAME GroupSummary a list row carries, built from a `members` answer — for a group opened before the list
 * knew it (a push, a deep link). Members become HUMAN participants, the `you` member names the viewer. A group that
 * is no longer active comes back flagged `ended` (it stays a group on screen). Null when nobody is in it.
 */
export function groupSummaryFromMembers(
  group: GroupInfo | null,
  members: GroupMember[],
  yourRole?: GroupRole | null,
  maestros: GroupMaestroWire[] = [],
  formerMembers: GroupMember[] = [],
): GroupSummary | null {
  if (!group || members.length === 0) return null;
  // A member the group's END removed reads themselves among the FORMER members (VMR #35004). iOS / Android do the same.
  const viewer = members.find((m) => m.you) ?? formerMembers.find((m) => m.you);
  const owner = members.find((m) => m.role === 'OWNER') ?? (group.ownerMetaSiteId ? members.find((m) => m.metaSiteId === group.ownerMetaSiteId) : undefined);
  const ownerId = group.ownerMetaSiteId ?? owner?.metaSiteId;
  return markOwnMaestro({
    conversationId: group.conversationId,
    ...(group.ownerMetaSiteId ? { ownerMetaSiteId: group.ownerMetaSiteId } : {}),
    ...(yourRole ?? viewer?.role ? { yourRole: (yourRole ?? viewer?.role) as GroupRole } : {}),
    name: group.name,
    ...(group.photoUrl ? { photoUrl: group.photoUrl } : {}),
    memberCount: members.length,
    ...(viewer ? { viewerId: viewer.metaSiteId } : {}),
    participants: [
      ...members.map(humanParticipant),
      // Phase 5: every Maestro the server says is in the group, each owner-labelled. A symphony-bff that predates
      // Phase 5 sends none, and the owner's Maestro is synthesized as in Phase 4 (the chats list does the same).
      ...(maestros.length > 0
        ? maestros.map((m) => maestroParticipant(m, members))
        : ownerId ? [ownerMaestroParticipant(ownerId, owner ? firstName(owner.name) : '')] : []),
    ],
    ...(formerMembers.length > 0 ? { formerParticipants: formerMembers.filter((m) => !members.some((x) => x.metaSiteId === m.metaSiteId)).map(humanParticipant) } : {}),
    ...(group.active ? {} : { ended: true as const }),
  });
}

function humanParticipant(m: GroupMember): GroupParticipant {
  return {
    id: m.metaSiteId,
    kind: 'HUMAN',
    displayName: m.name,
    role: m.role,
    ...(m.avatarUrl ? { avatarUrl: m.avatarUrl } : {}),
    ...(m.you ? { you: true } : {}),
  };
}

/** One Maestro as `members` answers it (Phase 5): its owner, and its name ('' = unnamed). */
export interface GroupMaestroWire {
  ownerMetaSiteId: string;
  name: string;
}
export function parseGroupMaestro(raw: unknown): GroupMaestroWire | null {
  if (!isObject(raw)) return null;
  const ownerMetaSiteId = str(raw.ownerMetaSiteId);
  if (!ownerMetaSiteId) return null;
  return { ownerMetaSiteId, name: typeof raw.name === 'string' ? raw.name : '' };
}

/** A `members` Maestro as the participant the chats list carries: `maestro:<owner>`, the owner's first name known. */
function maestroParticipant(m: GroupMaestroWire, members: GroupMember[]): GroupParticipant {
  const owner = members.find((x) => x.metaSiteId === m.ownerMetaSiteId);
  const ownerFirstName = owner ? firstName(owner.name) : '';
  return {
    id: `maestro:${m.ownerMetaSiteId}`,
    kind: 'MAESTRO',
    displayName: m.name,
    role: 'MEMBER',
    ownerId: m.ownerMetaSiteId,
    ...(ownerFirstName ? { ownerFirstName } : {}),
  };
}

/** The owner's Maestro as the chats list synthesizes it (Phase 4): named later by `nameMaestros`. */
export function ownerMaestroParticipant(ownerId: string, ownerFirstName: string): GroupParticipant {
  return {
    id: `maestro:${ownerId}`,
    kind: 'MAESTRO',
    displayName: '',
    role: 'MEMBER',
    ownerId,
    ...(ownerFirstName ? { ownerFirstName } : {}),
  };
}

/**
 * Names every participant the server sent without a name — THE SAME RULE on web, iOS and Android (Phase 5):
 * - the viewer's own Maestro → the viewer's own agent name (`ownAgentName`);
 * - someone else's, owner's first name known → "Dana's Maestro" (`chat.group.maestroOwnerName`), `defaultNamed`;
 * - no owner name → "Maestro" (`home.map.maestro`), `defaultNamed`;
 * - a PERSON with no name → their role's fallback (`personName`: "Group owner" / "Admin" / "Member"), `defaultNamed`,
 *   so every screen that reads `displayName` draws a name and initials, never a blank row over a "?" avatar.
 * A `defaultNamed` Maestro's label is that name alone. The same summary object when nothing needed naming.
 */
export function nameMaestros(summary: GroupSummary, ownAgentName: string, t: Translate): GroupSummary {
  // A Maestro's owner is named from the owner's own HUMAN row when the summary holds it (`ownerShortName`): it
  // OVERRIDES the wire's `ownerFirstName`, which takes the first word of a role fallback ("Group" of "Group owner").
  const ownerNames = new Map<string, string>();
  for (const p of summary.participants) {
    if (p.kind !== 'HUMAN') continue;
    const role: GroupRole = summary.ownerMetaSiteId && p.id === summary.ownerMetaSiteId ? 'OWNER' : p.role;
    ownerNames.set(p.id, ownerShortName({ displayName: p.displayName, role }, t));
  }
  const owned = summary.participants.map((p) => {
    if (p.kind !== 'MAESTRO' || !p.ownerId) return p;
    const name = ownerNames.get(p.ownerId);
    return name && name !== p.ownerFirstName ? { ...p, ownerFirstName: name } : p;
  });
  const changed = owned.some((p, i) => p !== summary.participants[i]);
  const base = changed ? { ...summary, participants: owned } : summary;
  if (!base.participants.some((p) => !p.displayName.trim())) return markOwnMaestro(base);
  return markOwnMaestro({
    ...base,
    participants: base.participants.map((p) => {
      if (p.displayName.trim()) return p;
      if (p.kind === 'HUMAN') {
        const role = base.ownerMetaSiteId && p.id === base.ownerMetaSiteId ? 'OWNER' : p.role;
        return { ...p, displayName: personName('', role, t), defaultNamed: true };
      }
      if (p.ownerId && p.ownerId === base.viewerId && ownAgentName.trim()) return { ...p, displayName: ownAgentName };
      if (p.ownerFirstName) return { ...p, displayName: t('chat.group.maestroOwnerName', { ownerFirstName: p.ownerFirstName }), defaultNamed: true };
      return { ...p, displayName: t('home.map.maestro'), defaultNamed: true };
    }),
  });
}

/** What a group message adds to a row besides `author`: the people it mentions and the message it answers. */
export interface GroupMessageMeta {
  groupPost?: true;
  mentions?: string[];
  replyToMessageId?: string;
  /** Phase 8: the Maestros a post or a Maestro's reply asks, each by its OWNER's metasite (typed MAESTRO mentions). */
  maestroMentions?: string[];
  /** Phase 8 SYSTEM line: a Maestro's public work was stopped (`metadata.groupWorkStopped`). */
  workStopped?: GroupWorkStopped;
  /** Phase 8 SYSTEM line: a chain of Maestro work paused at a bound (`metadata.groupChainStopped`). */
  chainStopped?: GroupChainStopped;
  /** R5 SYSTEM line: people were added directly (`metadata.groupSystemEvent`, kind MEMBERS_ADDED). */
  membersAdded?: GroupMembersAdded;
  /** Phase 10a: deleted for everyone (`metadata.groupDeleted`) — the row draws as a tombstone. */
  deleted?: GroupDeleted;
  /** Phase 10a SYSTEM line: the group's name, description, photo or settings changed, or someone joined by link. */
  infoEvent?: GroupInfoEvent;
  /** Phase 10b: one reaction per member (`metadata.groupReactions`). */
  reactions?: GroupReaction[];
  /** Phase 10b: the author edited the text (`metadata.groupEdited`). */
  edited?: GroupEdited;
  /** Phase 10b: pinned in the group (`metadata.groupPinned`), while it is. */
  pinned?: GroupPinned;
  /** Phase 10b: a forwarded copy (`metadata.groupForwarded`). */
  forwarded?: true;
  /** Phase 10c: a typed message — photo, video, document, voice, GIF, poll, event, place, contact, sticker (`metadata.groupContent`). */
  content?: GroupContent;
  /** Phase 10c: a poll's votes, one per member (`metadata.groupPollVotes`). */
  pollVotes?: GroupPollVote[];
  /** Phase 10c: an event's answers, one per member (`metadata.groupEventRsvps`). */
  eventRsvps?: GroupEventRsvp[];
}

/** Phase 10b: one member's reaction — `metadata.groupReactions[]`. */
export interface GroupReaction {
  metaSiteId: string;
  emoji: string;
  date: string | null;
}
/** Phase 10b: `metadata.groupEdited`. */
export interface GroupEdited {
  editedDate: string | null;
  count: number;
}
/** Phase 10b: `metadata.groupPinned`. */
export interface GroupPinned {
  pinnedByMetaSiteId: string | null;
  pinnedDate: string | null;
}

/** `metadata.groupReactions`, one per member (a later duplicate of a member replaces the earlier), no empty emoji. */
export function parseGroupReactions(metadata: unknown): GroupReaction[] {
  if (!isObject(metadata) || !Array.isArray(metadata.groupReactions)) return [];
  const byMember = new Map<string, GroupReaction>();
  for (const r of metadata.groupReactions) {
    if (!isObject(r)) continue;
    const metaSiteId = str(r.metaSiteId);
    const emoji = typeof r.emoji === 'string' ? r.emoji.trim() : '';
    if (!metaSiteId || !emoji) continue;
    byMember.delete(metaSiteId);
    byMember.set(metaSiteId, { metaSiteId, emoji, date: str(r.date) ?? null });
  }
  return [...byMember.values()];
}

/** `metadata.groupEdited`, or null (absent, or not an object). */
export function parseGroupEdited(metadata: unknown): GroupEdited | null {
  if (!isObject(metadata) || !isObject(metadata.groupEdited)) return null;
  const raw = metadata.groupEdited;
  const count = typeof raw.count === 'number' && Number.isFinite(raw.count) && raw.count > 0 ? Math.floor(raw.count) : 1;
  return { editedDate: str(raw.editedDate) ?? null, count };
}

/** `metadata.groupPinned`, or null. */
export function parseGroupPinned(metadata: unknown): GroupPinned | null {
  if (!isObject(metadata) || !isObject(metadata.groupPinned)) return null;
  return { pinnedByMetaSiteId: str(metadata.groupPinned.pinnedByMetaSiteId) ?? null, pinnedDate: str(metadata.groupPinned.pinnedDate) ?? null };
}

/** R5: who added whom. Ids are metasites; the line names them from the participant summary. */
export interface GroupMembersAdded {
  actorMetaSiteId: string;
  memberMetaSiteIds: string[];
}

/** `metadata.groupSystemEvent` of kind MEMBERS_ADDED, or null (another kind, no actor, or nobody added). */
export function parseGroupMembersAdded(metadata: unknown): GroupMembersAdded | null {
  if (!isObject(metadata) || !isObject(metadata.groupSystemEvent)) return null;
  const raw = metadata.groupSystemEvent;
  if (raw.kind !== 'MEMBERS_ADDED') return null;
  const actorMetaSiteId = str(raw.actorMetaSiteId);
  const memberMetaSiteIds = Array.isArray(raw.memberMetaSiteIds) ? raw.memberMetaSiteIds.filter((id): id is string => typeof id === 'string' && id.length > 0) : [];
  if (!actorMetaSiteId || memberMetaSiteIds.length === 0) return null;
  return { actorMetaSiteId, memberMetaSiteIds };
}

/** Phase 10a: `metadata.groupDeleted` — who deleted the message for everyone, and when. */
export interface GroupDeleted {
  deletedByMetaSiteId: string;
  deletedDate: string | null;
}

export type GroupInfoEventKind = 'NAME_CHANGED' | 'DESCRIPTION_CHANGED' | 'PHOTO_CHANGED' | 'PHOTO_REMOVED' | 'JOINED_BY_LINK' | 'SETTING_CHANGED' | 'MESSAGE_PINNED'
  | 'MEMBER_JOINED' | 'MEMBER_LEFT' | 'MEMBER_REMOVED' | 'GROUP_ENDED';
/**
 * Phase 10a: `metadata.groupEvent` carrying a `kind` (the membership events carry a `type` — {@link parseGroupEvent}).
 * `kind` and `setting` are kept as sent, so an unknown one still reads as an event and renders the row's own text.
 */
export interface GroupInfoEvent {
  kind: string;
  actorMetaSiteId: string | null;
  /** The actor's name as the server knew it — named only when the summary cannot (someone who has since left). */
  actorDisplayName?: string;
  /**
   * MEMBER_JOINED / MEMBER_LEFT / MEMBER_REMOVED: the member it is about (`memberMetaSiteId`, read tolerantly as
   * `subjectMetaSiteId` / `targetMetaSiteId` too), and that member's name as the server knew it.
   */
  subjectMetaSiteId?: string;
  subjectDisplayName?: string;
  value?: string;
  setting?: string;
  restricted?: boolean;
}

/** `metadata.groupDeleted`, or null (no deleter → not a tombstone). */
export function parseGroupDeleted(metadata: unknown): GroupDeleted | null {
  if (!isObject(metadata) || !isObject(metadata.groupDeleted)) return null;
  const deletedByMetaSiteId = str(metadata.groupDeleted.deletedByMetaSiteId);
  if (!deletedByMetaSiteId) return null;
  return { deletedByMetaSiteId, deletedDate: str(metadata.groupDeleted.deletedDate) ?? null };
}

/** `metadata.groupEvent` with a `kind` (Phase 10a), or null — a membership event (`type`) is not one. */
export function parseGroupInfoEvent(metadata: unknown): GroupInfoEvent | null {
  if (!isObject(metadata) || !isObject(metadata.groupEvent)) return null;
  const raw = metadata.groupEvent;
  const kind = str(raw.kind);
  if (!kind) return null;
  const value = typeof raw.value === 'string' ? raw.value : undefined;
  const setting = str(raw.setting);
  const actorDisplayName = str(raw.actorDisplayName)?.trim();
  const subjectMetaSiteId = str(raw.memberMetaSiteId) ?? str(raw.subjectMetaSiteId) ?? str(raw.targetMetaSiteId);
  const subjectDisplayName = (str(raw.memberDisplayName) ?? str(raw.subjectDisplayName) ?? str(raw.targetDisplayName))?.trim();
  return {
    kind,
    actorMetaSiteId: str(raw.actorMetaSiteId) ?? null,
    ...(actorDisplayName ? { actorDisplayName } : {}),
    ...(subjectMetaSiteId ? { subjectMetaSiteId } : {}),
    ...(subjectDisplayName ? { subjectDisplayName } : {}),
    ...(value !== undefined ? { value } : {}),
    ...(setting ? { setting } : {}),
    ...(typeof raw.restricted === 'boolean' ? { restricted: raw.restricted } : {}),
  };
}

export interface GroupWorkStopped {
  maestroOwnerMetaSiteId: string;
  stoppedByMetaSiteId?: string;
}
export type GroupChainStopReason = 'TOO_DEEP' | 'TOO_LONG' | 'TOO_MANY_TURNS' | 'NO_PROGRESS' | 'UNKNOWN';
export interface GroupChainStopped {
  chainId: string;
  reason: GroupChainStopReason;
  maestroOwnerMetaSiteId?: string;
}

/** `metadata.groupWorkStopped`, or null without the Maestro it names. */
export function parseGroupWorkStopped(metadata: unknown): GroupWorkStopped | null {
  if (!isObject(metadata) || !isObject(metadata.groupWorkStopped)) return null;
  const maestroOwnerMetaSiteId = str(metadata.groupWorkStopped.maestroOwnerMetaSiteId);
  if (!maestroOwnerMetaSiteId) return null;
  const stoppedByMetaSiteId = str(metadata.groupWorkStopped.stoppedByMetaSiteId);
  return { maestroOwnerMetaSiteId, ...(stoppedByMetaSiteId ? { stoppedByMetaSiteId } : {}) };
}

const CHAIN_STOP_REASONS: readonly GroupChainStopReason[] = ['TOO_DEEP', 'TOO_LONG', 'TOO_MANY_TURNS', 'NO_PROGRESS'];
/** `metadata.groupChainStopped`, or null without its chain; an unknown reason reads UNKNOWN (one line for all). */
export function parseGroupChainStopped(metadata: unknown): GroupChainStopped | null {
  if (!isObject(metadata) || !isObject(metadata.groupChainStopped)) return null;
  const raw = metadata.groupChainStopped;
  const chainId = str(raw.chainId);
  if (!chainId) return null;
  const reason = (CHAIN_STOP_REASONS as readonly unknown[]).includes(raw.reason) ? (raw.reason as GroupChainStopReason) : 'UNKNOWN';
  const maestroOwnerMetaSiteId = str(raw.maestroOwnerMetaSiteId);
  return { chainId, reason, ...(maestroOwnerMetaSiteId ? { maestroOwnerMetaSiteId } : {}) };
}

/** The typed MAESTRO entries of `metadata.mentions` — owner metasites, unique, in order. */
export function maestroMentionOwnerIds(metadata: unknown): string[] {
  if (!isObject(metadata) || !Array.isArray(metadata.mentions)) return [];
  const out: string[] = [];
  for (const m of metadata.mentions) {
    if (!isObject(m) || m.kind !== 'MAESTRO') continue;
    const id = str(m.metaSiteId);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

export function parseGroupMessageMeta(metadata: unknown): GroupMessageMeta | null {
  if (!isObject(metadata)) return null;
  const mentions = Array.isArray(metadata.mentions) ? metadata.mentions.filter((m): m is string => typeof m === 'string' && m.length > 0) : [];
  const replyToMessageId = str(metadata.replyToMessageId);
  const groupPost = metadata.groupPost === true;
  const maestroMentions = maestroMentionOwnerIds(metadata);
  const workStopped = parseGroupWorkStopped(metadata);
  const chainStopped = parseGroupChainStopped(metadata);
  const membersAdded = parseGroupMembersAdded(metadata);
  const deleted = parseGroupDeleted(metadata);
  const infoEvent = parseGroupInfoEvent(metadata);
  const reactions = parseGroupReactions(metadata);
  const edited = parseGroupEdited(metadata);
  const pinned = parseGroupPinned(metadata);
  const forwarded = metadata.groupForwarded === true;
  const content = parseGroupContent(metadata);
  const pollVotes = content?.type === 'POLL' ? parsePollVotes(metadata) : [];
  const eventRsvps = content?.type === 'EVENT' ? parseEventRsvps(metadata) : [];
  if (!content && !groupPost && mentions.length === 0 && !replyToMessageId && maestroMentions.length === 0 && !workStopped && !chainStopped && !membersAdded && !deleted && !infoEvent
    && reactions.length === 0 && !edited && !pinned && !forwarded) return null;
  return {
    ...(groupPost ? { groupPost: true as const } : {}),
    ...(mentions.length ? { mentions } : {}),
    ...(replyToMessageId ? { replyToMessageId } : {}),
    ...(maestroMentions.length ? { maestroMentions } : {}),
    ...(workStopped ? { workStopped } : {}),
    ...(chainStopped ? { chainStopped } : {}),
    ...(membersAdded ? { membersAdded } : {}),
    ...(deleted ? { deleted } : {}),
    ...(infoEvent ? { infoEvent } : {}),
    ...(reactions.length ? { reactions } : {}),
    ...(edited ? { edited } : {}),
    ...(pinned ? { pinned } : {}),
    ...(forwarded ? { forwarded: true as const } : {}),
    ...(content ? { content } : {}),
    ...(pollVotes.length ? { pollVotes } : {}),
    ...(eventRsvps.length ? { eventRsvps } : {}),
  };
}

/** The caller's role, from the summary: the server's `yourRole`, else the viewer's participant row. */
export function viewerRole(summary: GroupSummary): GroupRole | null {
  if (summary.yourRole) return summary.yourRole;
  const me = summary.viewerId ? summary.participants.find((p) => p.kind === 'HUMAN' && p.id === summary.viewerId) : undefined;
  return me?.role ?? null;
}
